import { Controller } from "@hotwired/stimulus";

// The form shape each price type forces, mirroring
// FORCED_FIELDS in
// app/views/components/admin/modifier_groups/form_component.rb:
// the server renders the current type's hidden fields,
// this rewrites them when the merchant switches type
// without reloading the page.
//
// An input inside a hidden block still submits whatever
// it holds, so every field a hidden block owns needs a
// forced field carrying the answer instead.
const PRICE_TYPE_SHAPES = {
  additive: {
    showsSelection: true,
    showsLimits: true,
    forced: {},
  },
  override: {
    showsSelection: false,
    showsLimits: false,
    forced: {
      selection_type: "single",
      required: "true",
      min: "1",
      max: "1",
    },
  },
  // Priced at the most expensive of SEVERAL selections,
  // so multiple and required are not the merchant's to
  // pick. How many halves a pizza is cut into is: the
  // counts stay on screen and editable.
  highest: {
    showsSelection: false,
    showsLimits: true,
    forced: {
      selection_type: "multiple",
      required: "true",
    },
  },
};

const DEFAULT_PRICE_TYPE = "additive";

// What a `highest` group's counts default to, matching
// the model's "maximum of 2 or more" rule. Applied only
// when the merchant switches type, never on connect:
// on a re-render the numbers on screen are the ones
// they just submitted, and rewriting them under the
// validation error that refused them would make the
// form contradict its own message.
const HIGHEST_DEFAULT_SELECTIONS = 2;

// This controller manages the modifier group form's behavior when the price
// type changes between additive, override and highest. It:
// - Shows/hides the blocks each type decides for the merchant
// - Updates required attributes
// - Forces the fields it took off screen
// - Notifies child modifier fields of the price type change
export default class extends Controller {
  static targets = [
    "form",
    "priceTypeInput",
    "selectionOptions",
    "selectionInput",
    "limitOptions",
    "limitInput",
    "forcedInput",
    "container",
    "field",
    "destroyInput",
    "sortButton",
  ];

  static values = {
    nameDirection: { type: String, default: "asc" },
    priceDirection: { type: String, default: "asc" },
  };

  connect() {
    // Set initial state based on the checked radio button
    this.priceTypeChanged();
  }

  // Handle invalid form fields by opening their parent accordion
  handleInvalidField(event) {
    // Find the closest modifier field container
    const modifierField = event.target.closest(
      "[data-nested-form-target='field']"
    );
    if (!modifierField) return;

    // Find the accordion checkbox and check it to open the accordion
    const accordionCheckbox = modifierField.querySelector(
      ".collapse input[type='checkbox']"
    );
    if (accordionCheckbox) {
      accordionCheckbox.checked = true;
    }
  }

  handleSubmit(event) {
    // Update positions before form submission
    this.updatePositions();
  }

  updatePositions() {
    // Get all non-destroyed modifier fields
    const fields = this.fieldTargets.filter((field) => {
      const destroyInput = field.querySelector(
        '[data-nested-form-target="destroyInput"]'
      );
      return !destroyInput || destroyInput.value !== "1";
    });

    // Update position inputs
    fields.forEach((field, index) => {
      // Find or create position input
      let positionInput = field.querySelector('input[name$="[position]"]');
      if (!positionInput) {
        positionInput = document.createElement("input");
        positionInput.type = "hidden";
        // Extract the proper name format from an existing input in the field
        const nameInput = field.querySelector('input[name$="[name]"]');
        const namePrefix = nameInput.name.replace("[name]", "");
        positionInput.name = `${namePrefix}[position]`;
        field.appendChild(positionInput);
      }
      positionInput.value = index;
    });
  }

  // `event` is absent when this runs from connect(),
  // which is how a re-render keeps the counts the
  // merchant typed.
  priceTypeChanged(event) {
    const priceType = this.selectedPriceType();
    const shape = PRICE_TYPE_SHAPES[priceType];

    this.selectionOptionsTarget.classList.toggle(
      "hidden",
      !shape.showsSelection
    );
    this.limitOptionsTarget.classList.toggle("hidden", !shape.showsLimits);

    this.selectionInputTargets.forEach((input) => {
      input.required = shape.showsSelection;
    });
    this.limitInputTargets.forEach((input) => {
      input.required = shape.showsLimits;
    });

    if (event && priceType === "highest") {
      this.applyHighestDefaults();
    }

    this.forcedInputTargets.forEach((input) => input.remove());
    Object.entries(shape.forced).forEach(([field, value]) => {
      this.appendForcedInput(field, value);
    });

    // Dispatch event to update modifier fields
    // This event is listened to by the modifier-fields controller
    // to update its UI based on the new price type
    this.dispatch("priceTypeChanged", {
      detail: { priceType },
    });
  }

  selectedPriceType() {
    const checked = this.priceTypeInputTargets.find(
      (input) => input.checked
    );
    const priceType = checked?.value ?? "";

    return Object.hasOwn(PRICE_TYPE_SHAPES, priceType)
      ? priceType
      : DEFAULT_PRICE_TYPE;
  }

  // A `highest` group needs at least two selections to
  // mean anything, so switching to it puts a mitad y
  // mitad on screen. Counts that already fit the type
  // are left alone: 1 to 2 halves and 3 thirds are both
  // real configurations.
  applyHighestDefaults() {
    const min = this.limitInput("min");
    const max = this.limitInput("max");
    if (!min || !max) return;
    if (parseInt(max.value, 10) >= HIGHEST_DEFAULT_SELECTIONS) return;

    min.value = HIGHEST_DEFAULT_SELECTIONS;
    max.value = HIGHEST_DEFAULT_SELECTIONS;
  }

  limitInput(field) {
    return this.limitInputTargets.find((input) =>
      input.name.endsWith(`[${field}]`)
    );
  }

  appendForcedInput(field, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `modifier_group[${field}]`;
    input.value = value;
    input.dataset.modifierGroupFormTarget = "forcedInput";
    input.dataset.testid = `forced-${field.replaceAll("_", "-")}`;
    this.formTarget.appendChild(input);
  }

  toggleNameSort(event) {
    event.preventDefault();
    const isAsc = this.nameDirectionValue === "asc";

    // Update button icons
    this.sortButtonTargets.forEach((button) => {
      if (button.dataset.sort === "name") {
        const icon = button.querySelector("svg");
        icon.style.transform = isAsc ? "rotate(180deg)" : "rotate(0deg)";
      }
    });

    if (isAsc) {
      this.sortModifiers((a, b) => {
        const nameA = this.getModifierName(a);
        const nameB = this.getModifierName(b);
        return nameB.localeCompare(nameA);
      });
      this.nameDirectionValue = "desc";
    } else {
      this.sortModifiers((a, b) => {
        const nameA = this.getModifierName(a);
        const nameB = this.getModifierName(b);
        return nameA.localeCompare(nameB);
      });
      this.nameDirectionValue = "asc";
    }
  }

  togglePriceSort(event) {
    event.preventDefault();
    const isAsc = this.priceDirectionValue === "asc";

    // Update button icons
    this.sortButtonTargets.forEach((button) => {
      if (button.dataset.sort === "price") {
        const icon = button.querySelector("svg");
        icon.style.transform = isAsc ? "rotate(180deg)" : "rotate(0deg)";
      }
    });

    if (isAsc) {
      this.sortModifiers((a, b) => {
        const priceA = this.getModifierPrice(a);
        const priceB = this.getModifierPrice(b);
        return priceB - priceA;
      });
      this.priceDirectionValue = "desc";
    } else {
      this.sortModifiers((a, b) => {
        const priceA = this.getModifierPrice(a);
        const priceB = this.getModifierPrice(b);
        return priceA - priceB;
      });
      this.priceDirectionValue = "asc";
    }
  }

  sortModifiers(compareFn) {
    if (!this.hasContainerTarget) {
      throw new Error("Modifier container not found");
    }

    const modifiers = this.fieldTargets.filter((el) => {
      const destroyInput = el.querySelector(
        '[data-nested-form-target="destroyInput"]'
      );
      return !destroyInput || destroyInput.value !== "1";
    });

    if (modifiers.length === 0) {
      console.warn("No modifiers found to sort");
      return;
    }

    modifiers.sort(compareFn);

    modifiers.forEach((modifier) => {
      this.containerTarget.appendChild(modifier);
    });
  }

  getModifierName(element) {
    const nameInput = element.querySelector('input[name$="[name]"]');
    const name = nameInput?.value || "";
    return name.toLowerCase().trim();
  }

  getModifierPrice(element) {
    const priceInput = element.querySelector('input[name$="[price]"]');
    const price = parseFloat(priceInput?.value || "0");
    return isNaN(price) ? 0 : price;
  }
}
